
import { supabase } from '@/integrations/supabase/client';

export interface UserWallet {
  id: string;
  user_id: string;
  balance: number;
  created_at: string;
  updated_at: string;
}

export interface Transaction {
  id: string;
  user_id: string;
  amount: number;
  type: 'credit' | 'debit';
  type_category?: string;
  description?: string;
  reference_id?: string;
  created_at: string;
}

export class WalletRepository {
  
  async getUserWallet(userId: string): Promise<UserWallet | null> {
    try {
      console.log('🔍 Buscando carteira do usuário:', userId);
      
      const { data, error } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('❌ Erro ao buscar carteira:', error);
        return null;
      }

      console.log('✅ Carteira encontrada:', data);
      return data;
    } catch (error) {
      console.error('❌ Erro ao buscar carteira:', error);
      return null;
    }
  }

  async getCurrentUserWalletBalance(): Promise<number> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return 0;

      const wallet = await this.getUserWallet(session.user.id);
      return wallet?.balance || 0;
    } catch (error) {
      console.error('❌ Erro ao buscar saldo:', error);
      return 0;
    }
  }

  async refreshWalletBalance(): Promise<number> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return 0;

      // Get fresh balance from database
      const { data, error } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', session.user.id)
        .single();

      if (error) {
        console.error('❌ Erro ao atualizar saldo:', error);
        return 0;
      }

      console.log('🔄 Saldo atualizado:', data?.balance || 0);
      return data?.balance || 0;
    } catch (error) {
      console.error('❌ Erro ao atualizar saldo:', error);
      return 0;
    }
  }

  async getTransactionHistory(userId: string, limit: number = 50): Promise<Transaction[]> {
    try {
      console.log('🔍 Buscando histórico de transações para usuário:', userId);
      
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('❌ Erro ao buscar histórico de transações:', error);
        return [];
      }

      console.log('✅ Transações encontradas:', data?.length || 0);
      
      // Ensure type safety by casting and filtering
      const typedTransactions: Transaction[] = (data || []).map(item => ({
        ...item,
        type: (item.type === 'credit' || item.type === 'debit') ? item.type : 'credit'
      })) as Transaction[];
      
      return typedTransactions;
    } catch (error) {
      console.error('❌ Erro ao buscar histórico de transações:', error);
      return [];
    }
  }

  async getCurrentUserRecentTransactions(limit: number = 5): Promise<Transaction[]> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return [];

      return this.getTransactionHistory(session.user.id, limit);
    } catch (error) {
      console.error('❌ Erro ao buscar transações recentes:', error);
      return [];
    }
  }

  async addSeshs(userId: string, amount: number, description: string = 'Crédito manual'): Promise<boolean> {
    try {
      console.log('➕ Adicionando seshs para usuário:', userId, 'quantidade:', amount);
      
      const result = await supabase.rpc('add_seshs_to_user_simple', {
        p_user_id: userId,
        p_amount: amount,
        p_description: description
      });

      if (result.error) throw result.error;

      console.log('✅ Seshs adicionados com sucesso');
      
      // Type-safe parsing of JSON response
      const response = result.data as any;
      return response?.success === true;
    } catch (error) {
      console.error('❌ Erro ao adicionar seshs:', error);
      return false;
    }
  }
}
